import React, { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import { getBillingTypeDisplayName } from '../../util/gearOrderUtil';
import AlertMessage from '../../AlertMessage';
import { getDefaultUkBillingOption } from '../../BillingOptions';

/**
 * A modal to select the billing method
 */
const BillingMethodModal = ({ billingOptions, practitioner, selectedBillingMethod, onModalHidden, onSave }) => {
	const [billingMethod, setBillingMethod] = useState(
		selectedBillingMethod || getDefaultBillingMethod({ practitioner, billingOptions })
	);
	const [partialSupport, setPartialSupport] = useState(false);
	const [subOptionRequired, setSubOptionRequired] = useState(false);

	const handleSave = () => {
		if (billingMethod?.endsWith(':')) {
			setSubOptionRequired(true);
			return;
		}
		onSave(billingMethod);
		onModalHidden();
	};

	const changeBilling = (e) => {
		setBillingMethod(e?.target?.value || '');
	};

	// Show a message if any product in the bundle does not support the selected billing method
	useEffect(() => {
		setPartialSupport(
			!!(billingMethod && billingOptions?.find((option) => option.name === billingMethod)?.partialSupport)
		);
		if (!billingMethod?.endsWith(':')) {
			setSubOptionRequired(false);
		}
	}, [billingMethod, billingOptions]);

	return (
		<Modal
			id={`billing-customization-modal`}
			displayProps={{ modalContent: { className: 'modal-content modal-scroll' } }}
			title="Select a Billing Method"
			modalHidden={onModalHidden}
			footerActionEl={
				<>
					<button type="button" className="btn btn-light" onClick={onModalHidden}>
						Cancel
					</button>
					<button type="button" className="btn btn-primary" disabled={subOptionRequired} onClick={handleSave}>
						Save
					</button>
				</>
			}
		>
			{partialSupport && (
				<AlertMessage type="info" additionalCssClass="mt-0">
					One or more of the products on your custom profile does not support the selected billing method. You
					will be required to choose a billing method when placing an order.
				</AlertMessage>
			)}
			<div className="mt-20">
				<div className="list-group">
					{billingOptions.map((option) => {
						return (
							<BillingOptionRadio
								key={option.name}
								option={option}
								selectedBillingMethod={billingMethod}
								onChange={changeBilling}
							/>
						);
					})}
				</div>
			</div>
			{subOptionRequired && (
				<p className="validation-message mt-3 mb-0" style={{ color: '#a94442' }}>
					Please select a Patient Pay Wholesale or Retail price option.
				</p>
			)}
		</Modal>
	);
};

const BillingOptionRadio = ({ option, selectedBillingMethod, onChange }) => {
	const optionName = getBillingTypeDisplayName(option.name);
	let hasSubOptions = option.subBillingOptions?.subOptions?.length > 0;

	// The option, or one of its sub-options, is selected
	const isSelected =
		selectedBillingMethod === option.name ||
		(hasSubOptions && selectedBillingMethod.startsWith(option.name + ':')) ||
		false;

	/**
	 * For billing options with sub-options, add a colon to the value to indicate that a sub-option
	 * needs to be selected. Submit will be disabled if the selected option ends with a colon.
	 *
	 * If there is only a single sub-option, include it in the main radio button's value and don't
	 * show the sub-options.
	 */
	let optionValue = option.name;
	if (hasSubOptions) {
		optionValue += ':';
		if (option.subBillingOptions.subOptions.length === 1) {
			optionValue += option.subBillingOptions.subOptions[0];
			hasSubOptions = false; // No need to show sub-options if only one exists
		}
	}

	return (
		<label className={'list-group-item ' + (isSelected ? 'active' : '')} key={option.name}>
			<div>
				<input
					type="radio"
					value={optionValue}
					name="billingOptions"
					onChange={onChange}
					checked={isSelected}
				/>
				<div className="billing-option-text">
					<h4 className="list-group-item-heading control-label">{optionName}</h4>
					{!!option.description && <p className="list-group-item-text">{option.description}</p>}
					{isSelected && hasSubOptions && (
						<BillingSubOptions
							subOptions={option.subBillingOptions.subOptions}
							selectedBillingMethod={selectedBillingMethod}
							onChange={onChange}
							title={option.subBillingOptions.title}
						/>
					)}
				</div>
			</div>
		</label>
	);
};

const BillingSubOptions = ({ subOptions, selectedBillingMethod, onChange, title }) => {
	const [selectedOption, selectedSubOption] = selectedBillingMethod.split(':');

	return (
		<>
			<div className="list-group-sub-item-heading">{title}</div>
			{subOptions.map((subOption) => (
				<div key={subOption}>
					<label className="list-group-item-heading control-label">
						<div>
							<input
								type="radio"
								className={'pull-left form-check-input'}
								value={`${selectedOption}:${subOption}`}
								name="subBillingOptions"
								onChange={onChange}
								checked={selectedSubOption === subOption}
							/>
							<span className="billing-option-text">{subOption}</span>
						</div>
					</label>
				</div>
			))}
		</>
	);
};

const getDefaultBillingMethod = ({ practitioner, billingOptions }) => {
	let billingMethod = '';
	let subOption = null;
	if (practitioner?.ukclient) {
		({ billingMethod = '', subOption } = getDefaultUkBillingOption({
			practitioner: practitioner,
			availableBillingOptions: billingOptions
		}));
	}

	return billingMethod + (subOption ? `:${subOption}` : '');
};

export default BillingMethodModal;
